/**
 * useSandboxSuggestions — sandbox-compatible replacement for useSuggestions.
 *
 * Same interface as the internal useSuggestions hook, but reads vendor
 * recommendations from the published sandbox config (localStorage)
 * instead of making API calls to the procurement backend.
 */

import { useCallback, useMemo } from 'react';
import { SANDBOX_CONFIG_KEY } from '../../config/zeroTrustConstants';
import { getSuggestion as getHardcodedSuggestion } from '../services/gapSuggestions';

function getPublishedConfig() {
  try {
    const raw = localStorage.getItem(SANDBOX_CONFIG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function useSandboxSuggestions() {
  // Build suggestions map from published config recommendations
  const suggestions = useMemo(() => {
    const config = getPublishedConfig();
    const map = {};

    if (config?.pillars) {
      config.pillars.forEach(pillar => {
        (pillar.capabilities || []).forEach(cap => {
          const recs = cap.recommendations || [];
          if (recs.length > 0) {
            const top = recs[0];
            map[cap.code] = {
              topVendor: top.vendor,
              topProduct: top.product,
              confidence: top.coverage === 'STRONG' ? 90 : top.coverage === 'PARTIAL' ? 60 : 30,
              totalSpend: 0,
              productCount: recs.length,
              detected: recs.map(r => ({
                vendor: r.vendor,
                product: r.product,
                coverage: r.coverage,
              })),
            };
          } else {
            // Fall back to hardcoded suggestions
            const hardcoded = getHardcodedSuggestion(cap.name);
            if (hardcoded) {
              map[cap.code] = {
                topVendor: hardcoded.products[0]?.split(' ')[0] || hardcoded.products[0],
                topProduct: hardcoded.products[0],
                confidence: 70,
                totalSpend: 0,
                productCount: hardcoded.products.length,
                detected: hardcoded.products.map(p => ({
                  vendor: p.split(' ')[0],
                  product: p,
                  coverage: 'PARTIAL',
                })),
              };
            }
          }
        });
      });
    }

    return map;
  }, []);

  const getSuggestionForCapability = useCallback((capabilityCode) => {
    if (!capabilityCode) return null;
    const code = capabilityCode.toLowerCase();
    const suggestion = suggestions[code];
    if (!suggestion || !suggestion.topVendor) return null;

    return {
      vendor: suggestion.topVendor,
      product: suggestion.topProduct,
      confidence: suggestion.confidence || 0,
      totalSpend: suggestion.totalSpend || 0,
      productCount: suggestion.productCount || 0,
      detected: suggestion.detected || [],
    };
  }, [suggestions]);

  const fetchTransactions = useCallback(async () => {
    // No transaction data in sandbox mode
    return { transactions: [], count: 0, totalSpend: 0 };
  }, []);

  const hasSuggestion = useCallback((capabilityCode) => {
    return getSuggestionForCapability(capabilityCode) !== null;
  }, [getSuggestionForCapability]);

  return {
    suggestions,
    loading: false,
    error: null,
    getSuggestionForCapability,
    fetchTransactions,
    hasSuggestion,
    refresh: () => {},
  };
}
